import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, Grid, MenuItem, Select, makeStyles } from '@material-ui/core';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { TabNativeContent } from '../common/Tab';
import service from '../../apis/service';
import { clearPxDashboardFilters, setPxDashboardFilters, showProgress } from '../../redux/actions/DealershipActions';
import { ErrorToast, formatDate, notifySuccess } from '../utils/Utils';
import ShowProgress from '../ui/Backdrop';
import PXFilterBar from './PXFilterBar';
import PXOverview from './PXOverview';
import VirtualizedTable from '../common/VirtualizedTable';
import { CustomDatePopup } from './CustomDatePopup';
import { apiEndpoints } from '../../constants/apiEndpoints';
import { IndividualDealership } from './IndividualDealership';
import { InsightPopup } from './InsightPopup';

const PERIOD_ARR = ['Last Month', 'Last Week', 'This Week', 'This Month', 'Custom Date'];

const useStyles = makeStyles(() => ({
  tabs: {
    backgroundColor: 'white',
    border: '1px solid #E2E2E2',
  },
  topGrid: {
    minHeight: '10vh',
    maxHeight: '40vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
  },

  customInputUnderline: {
    '&:before': {
      borderBottom: 'none !important',
    },
    '&:after': {
      borderBottom: 'none !important',
    },
  },
}));

export default function PXMainContent() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const pxDashboardFilters = useSelector((state) => state.pxDashboardData.filters);
  const pxUserSelected = useSelector((state) => state.pxDashboardData.selectedUser);
  const [dateRange, setDateRange] = useState(null);
  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [individualDealerId, setIndividualDealerId] = useState(null);

  const DEFAULT_FILTERS = useMemo(() => {
    const sales = pxDashboardFilters?.salesPipelineStatus?.find((i) => i.title === 'Active') || null;
    const tempAllDealerships = {};
    pxDashboardFilters?.dealerships?.forEach((i) => {
      tempAllDealerships[i.id] = i.children.map((j) => j.id);
    });

    return {
      salesPipelineStatus: sales ? [sales] : [],
      timePeriod: 3,
      source: pxDashboardFilters?.source?.map((item) => item.source) || [],
      dealerships: tempAllDealerships,
    };
  }, [pxDashboardFilters]);

  useEffect(() => {
    setAppliedFilters(DEFAULT_FILTERS);
    fetchERData(DEFAULT_FILTERS, false);
  }, [DEFAULT_FILTERS]);

  const [erData, setErData] = useState({});
  const [tabCount, setTabCount] = useState({
    allDealershipCount: 0,
    bigLoserDealershipCount: 0,
    bigMoverDealershipCount: 0,
    newlyLaunchedDealershipCount: 0,
    dsltPoorDealershipCount: 0,
  });
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [tabId, setTabId] = useState(0);
  const [dropdownFilterId, setDropdownFilterId] = useState(0);
  const [openInsightPopup, setOpenInsightPopup] = useState(false);
  const [timePeriod, setTimePeriod] = useState(0);
  const [selctedDealerId, setSelectedDealerId] = useState(null);

  const isButtonDisabled = useMemo(
    () => JSON.stringify(DEFAULT_FILTERS) === JSON.stringify(appliedFilters),
    [DEFAULT_FILTERS, appliedFilters],
  );

  const TABS = useMemo(
    () => [
      {
        label: `Dealerships (${dropdownFilterId > 0 ? erData?.list?.length || 0 : tabCount.allDealershipCount})`,
        index: 0,
      },
      { label: `Big Movers (${tabCount.bigMoverDealershipCount})`, index: 1 },
      { label: `Big Losers (${tabCount.bigLoserDealershipCount})`, index: 2 },
      { label: `Communication > 30 days (${tabCount.dsltPoorDealershipCount})`, index: 3 },
      { label: `Newly Launched Dealerships (${tabCount.newlyLaunchedDealershipCount})`, index: 4 },
    ],
    [tabCount, erData],
  );
  const INSIGHT_TABS = [
    { label: `Zero Activity`, index: 0 },
    { label: `Learning Activity`, index: 1 },
    { label: `App Config`, index: 2 },
    { label: `Rewards`, index: 3 },
    { label: `Leaderboard`, index: 4 },
  ];

  const DROPDOWN_FILTER = [
    { label: `All Dealerships`, index: 0 },
    { label: `Top 20 Dealerships`, index: 1 },
    { label: `Bottom 20 Dealerships`, index: 2 },
  ];

  const fetchFilters = async () => {
    try {
      const response = await service.post(apiEndpoints.PX_FILTERS, {
        ...(pxUserSelected.name !== 'All' ? { pxOwnerHash: pxUserSelected.hash } : {}),
      });
      dispatch(setPxDashboardFilters(response));
    } catch (err) {
      ErrorToast(err);
    }
  };

  const fetchERData = async (defaultFilters, isFiltersApplied = true) => {
    const sales = (
      defaultFilters?.salesPipelineStatus?.length
        ? defaultFilters.salesPipelineStatus
        : appliedFilters.salesPipelineStatus
    ).map((i) => i.id);

    const dealerships = Object.keys(defaultFilters?.dealerships || {}).length
      ? defaultFilters.dealerships
      : appliedFilters.dealerships;

    const source = defaultFilters?.source?.length ? defaultFilters.source : appliedFilters.source;

    if (!sales.length || _isEmpty(dealerships)) {
      setErData([]);
      return;
    }

    const time = {
      type: !_isEmpty(defaultFilters) ? defaultFilters.timePeriod + 1 : appliedFilters.timePeriod + 1,
      ...(appliedFilters.timePeriod === 4 && _isEmpty(defaultFilters)
        ? {
            startDate: formatDate(appliedFilters.range.startDate),
            endDate: formatDate(appliedFilters.range.endDate),
          }
        : {}),
    };

    dispatch(showProgress(true));
    const filters = {
      filters: {
        enabled: true,
        trendRequired: false,
        time,
        salesPipelineStatus: sales,
        source,
        ...(pxUserSelected.name !== 'All' ? { pxOwnerHash: pxUserSelected.hash } : {}),
      },
    };

    if (isFiltersApplied) {
      filters.filters.dealerships = dealerships;
    }

    if (tabId === 1) {
      filters.filters.type = 'BIG_MOVERS';
    } else if (tabId === 2) {
      filters.filters.type = 'BIG_LOSERS';
    } else if (tabId === 3) {
      filters.filters.type = 'DSLT_POOR';
    } else if (tabId === 4) {
      filters.filters.type = 'NEW';
    }

    if (dropdownFilterId === 1) {
      filters.filters.limit = 20;
      filters.filters.type = 'TOP';
    } else if (dropdownFilterId === 2) {
      filters.filters.limit = 20;
      filters.filters.type = 'BOTTOM';
    }

    if (
      appliedFilters.timePeriod !== 4 ||
      (appliedFilters.timePeriod === 4 && !_isEmpty(appliedFilters.range) && !appliedFilters.range.initial)
    ) {
      try {
        const response = await service.post(apiEndpoints.PX_REPORT, filters);
        setErData(response || {});
      } catch (error) {
        ErrorToast(error);
      }
      dispatch(showProgress(false));

      try {
        const response = await service.post(apiEndpoints.PX_TAB_COUNT, filters);
        setTabCount(response || {});
      } catch (err) {
        ErrorToast(err);
      }
    } else {
      dispatch(showProgress(false));
    }
  };

  const handleClearFilters = (flag = true) => {
    setDateRange(null);
    setAppliedFilters(DEFAULT_FILTERS);
    if (flag) {
      fetchERData(DEFAULT_FILTERS, false);
    }
  };

  useEffect(
    () => () => {
      dispatch(clearPxDashboardFilters());
    },
    [],
  );

  useEffect(() => {
    handleClearFilters(false);
    if (!_isEmpty(pxUserSelected)) {
      fetchFilters();
    }
  }, [pxUserSelected]);

  useEffect(() => {
    fetchERData();
  }, [tabId, dropdownFilterId]);

  useEffect(() => {
    setTimePeriod(appliedFilters?.timePeriod);
  }, [appliedFilters]);

  const handleApplyFilter = () => {
    if (appliedFilters.salesPipelineStatus.length) {
      fetchERData();
    } else {
      setErData([]);
    }
  };

  const handleDropdonwFilter = (value) => {
    setTabId(0);
    setDropdownFilterId(value);
  };

  const handleTabId = (id) => {
    setDropdownFilterId(0);
    setTabId(id);
  };

  const handleFilterChange = (name, value) => {
    if (name === 'salesPipelineStatus') {
      if (value.length === 0) {
        setAppliedFilters((app) => ({
          ...app,
          salesPipelineStatus: DEFAULT_FILTERS.salesPipelineStatus,
        }));
      } else {
        setAppliedFilters((app) => ({
          ...app,
          salesPipelineStatus: value,
        }));
      }
    } else if (name === 'source') {
      if (value.length === 0) {
        setAppliedFilters((app) => ({
          ...app,
          source: ['ADMIN'],
        }));
      } else {
        setAppliedFilters((app) => ({
          ...app,
          source: value,
        }));
      }
    } else if (name === 'time') {
      setDateRange(null);
      if (value === 4) {
        setIsOpenCalendar(true);
      }

      setAppliedFilters((app) => {
        const temp = { ...app };
        delete temp.range;
        return {
          ...app,
          timePeriod: value,
          ...(value === 4
            ? {
                range: {
                  initial: true,
                },
              }
            : {}),
        };
      });
    } else {
      // dealerships
      setAppliedFilters((app) => ({
        ...app,
        [name]: value,
      }));
    }
  };

  const handleRangeSelection = (range) => {
    setDateRange({
      startDate: moment(range.startDate).format('DD-MM-YYYY'),
      endDate: moment(range.endDate).format('DD-MM-YYYY'),
    });
    setAppliedFilters((app) => ({
      ...app,
      range,
    }));
  };

  const handleEmailReport = async (type) => {
    dispatch(showProgress(true));

    const filters = JSON.stringify({
      filters: {
        dealerships: erData.filters.dealerships,
        ...(type === 'TOM_REPORT' ? {} : { trendRequired: false }),
        time: {
          ...(appliedFilters.timePeriod === 4
            ? {
                startDate: formatDate(appliedFilters.range.startDate),
                endDate: formatDate(appliedFilters.range.endDate),
              }
            : {}),
          type: appliedFilters.timePeriod + 1,
        },
      },
    });

    if (type === 'PX_REPORT') {
      try {
        await service.post(apiEndpoints.PX_REPORT_EMAIL, filters);
        notifySuccess('PX Report Email Sent Successfully');
      } catch (err) {
        ErrorToast(err);
      }
    } else if (type === 'TOM_REPORT') {
      try {
        const response = await service.post(apiEndpoints.REPORT_CHECKIN_REPORT, filters);
        if (response.message) {
          notifySuccess(response.message);
        }
        dispatch(showProgress(false));
      } catch (err) {
        ErrorToast(err);
        dispatch(showProgress(false));
      }
    } else {
      try {
        await service.post(apiEndpoints.PX_EMAIL, filters);
        notifySuccess('Usage Report Email Sent Successfully');
      } catch (err) {
        ErrorToast(err);
      }
    }
    dispatch(showProgress(false));
  };

  const handleAllSourcesToggle = (isCheck) => {
    if (isCheck) {
      setAppliedFilters((app) => ({
        ...app,
        source: pxDashboardFilters?.source?.map((item) => item.source) || [],
      }));
    } else {
      setAppliedFilters((app) => ({
        ...app,
        source: ['ADMIN'],
      }));
    }
  };

  const handleAllPipelineStatus = (e) => {
    const isCheck = e.target.checked;
    if (isCheck) {
      setAppliedFilters((app) => ({
        ...app,
        salesPipelineStatus: pxDashboardFilters?.salesPipelineStatus?.map((item) => item),
      }));
    } else {
      setAppliedFilters((app) => ({
        ...app,
        salesPipelineStatus: DEFAULT_FILTERS.salesPipelineStatus,
      }));
    }
  };

  const handleOpenInsightPopup = (id) => {
    setSelectedDealerId(id);
    setOpenInsightPopup(true);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const FilterComponent = () => (
    <FormControl
      style={{
        width: '250px',
        height: '50px',
        background: '#f5f5f6',
        borderRadius: '5px',
        padding: '0px 8px',
      }}
    >
      <Select
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={dropdownFilterId}
        onChange={(e) => handleDropdonwFilter(e.target.value)}
        style={{ height: '50px', textAlign: 'left', border: 'none' }}
        className={classes.customInputUnderline}
      >
        {DROPDOWN_FILTER.map((item) => (
          <MenuItem value={item.index} style={{ textAlign: 'left' }}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Grid container direction="column">
      <Grid item className={classes.topGrid}>
        <Grid container direction="column">
          <PXFilterBar
            appliedFilters={appliedFilters}
            pxDashboardFilters={pxDashboardFilters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            handleApplyFilter={handleApplyFilter}
            PERIOD_ARR={PERIOD_ARR}
            isButtonDisabled={isButtonDisabled}
            dateRange={dateRange}
            handleAllSourcesToggle={handleAllSourcesToggle}
            handleAllPipelineStatus={handleAllPipelineStatus}
            DEFAULT_FILTERS={DEFAULT_FILTERS}
            setAppliedFilters={setAppliedFilters}
          />
          <PXOverview erData={erData} />
        </Grid>
      </Grid>
      <br />

      {!openInsightPopup && !individualDealerId && (
        <Grid item className={classes.tabs}>
          <TabNativeContent tabValue={tabId} setTabValue={(id) => handleTabId(id)} tabs={TABS} tabWidth="18%" />
          <VirtualizedTable
            data={erData}
            handleEmailReport={handleEmailReport}
            DROPDOWN_FILTER={DROPDOWN_FILTER}
            tabId={tabId}
            handleOpenInsightPopup={handleOpenInsightPopup}
            batchSize={20}
            customFilterComponent={FilterComponent}
            setIndividualDealerId={setIndividualDealerId}
          />
        </Grid>
      )}

      <ShowProgress />
      {isOpenCalendar ? (
        <CustomDatePopup
          appliedFilters={appliedFilters}
          handleRangeSelection={handleRangeSelection}
          setIsOpenCalendar={setIsOpenCalendar}
          setAppliedFilters={setAppliedFilters}
        />
      ) : null}

      {individualDealerId && (
        <IndividualDealership
          PERIOD_ARR={PERIOD_ARR}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          dealerId={individualDealerId}
          setDealerId={setIndividualDealerId}
          handleOpenInsightPopup={handleOpenInsightPopup}
        />
      )}

      {openInsightPopup && (
        <InsightPopup
          setOpenInsightPopup={setOpenInsightPopup}
          PERIOD_ARR={PERIOD_ARR}
          INSIGHT_TABS={INSIGHT_TABS}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          selctedDealerId={selctedDealerId}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
        />
      )}
    </Grid>
  );
}
